import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { clientsApi, quotationsApi } from '../../api/endpoints.js';
import type { DuplicateSearchResult } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxField,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { DuplicateMatchList } from '../clients/DuplicateMatchList.js';

/**
 * Turn the prospect on an accepted quotation into a client file.
 *
 * NOTHING IS RE-ENTERED. The details shown here are the ones already on the
 * quotation, and they are what becomes the file — this page collects no
 * client fields at all, which is the whole point of the acceptance criterion.
 *
 * THE DUPLICATE CHECK IS THE SAME ONE the ordinary client form uses, called
 * through the same endpoint and the same token. A match at `exact` or `strong`
 * confidence must be acknowledged by id; a `possible` one is shown and gates
 * nothing. It WARNS — it never hard-blocks, because a desk that cannot finish
 * the job is a worse failure than a duplicate somebody merges next week
 * (decision D36).
 */
export function QuotationConvertPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();

  const [search, setSearch] = useState<DuplicateSearchResult | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const quotation = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => quotationsApi.get(id as string),
  });

  /**
   * Run the duplicate search from the details ALREADY on the quotation.
   *
   * The search must happen before the file can be created — the create path
   * demands a token proving it did — and running it from the quotation's own
   * details is what makes "without re-entry" true rather than nearly true.
   */
  const runSearch = useMutation({
    mutationFn: () => {
      const party = quotation.data?.party;
      return clientsApi.searchDuplicates({
        ...(party?.name ? { name: party.name } : {}),
        ...(party?.contactEmail ? { contactEmail: party.contactEmail } : {}),
        ...(party?.contactPhone ? { contactPhone: party.contactPhone } : {}),
      });
    },
    onSuccess: (result) => setSearch(result),
  });

  useEffect(() => {
    if (quotation.data && !search && !runSearch.isPending) {
      runSearch.mutate();
    }
    // Intentionally keyed on the loaded quotation only: the search runs once
    // per visit, and re-running it on every render would mint tokens nobody
    // asked for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation.data]);

  const convert = useMutation({
    mutationFn: () =>
      quotationsApi.convertProspect(id as string, {
        duplicateSearchToken: search?.searchToken,
        ...(acknowledged.length > 0
          ? { acknowledgedDuplicateIds: acknowledged }
          : {}),
        ...(note.trim() ? { duplicateAcknowledgementNote: note.trim() } : {}),
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['quotation', id] });
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      show({ tone: 'success', message: t('quotations.converted') });
      navigate(`/clients/${saved.convertedClientId}`);
    },
  });

  /**
   * Link to a client already on file instead of creating a second record.
   *
   * Offered on every candidate the search returned, because the commonest
   * outcome of a real duplicate warning is not "acknowledge and create a
   * second one" — it is "that is them".
   */
  const linkExisting = useMutation({
    mutationFn: (existingClientId: string) =>
      quotationsApi.convertProspect(id as string, {
        duplicateSearchToken: search?.searchToken,
        existingClientId,
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['quotation', id] });
      show({ tone: 'success', message: t('quotations.converted') });
      navigate(`/clients/${saved.convertedClientId}`);
    },
  });

  if (quotation.isLoading) return <LoadingState label={t('common.loading')} />;
  if (quotation.error) {
    return (
      <ErrorState
        error={quotation.error}
        onRetry={() => void quotation.refetch()}
      />
    );
  }

  const record = quotation.data;
  if (!record) return null;

  const duplicateRefusal =
    convert.error instanceof ApiError &&
    convert.error.code === 'DUPLICATE_RECORD';

  return (
    <>
      <PageHeader
        title={t('quotations.convertTitle')}
        subtitle={t('quotations.convertHint')}
        breadcrumbs={[
          { label: t('nav.quotations'), to: '/quotations' },
          { label: record.quotationNumber, to: `/quotations/${record.id}` },
          { label: t('quotations.convertTitle') },
        ]}
      />

      {duplicateRefusal ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('clients.duplicate.blockedTitle')}
            description={t('clients.duplicate.blockedBody')}
          />
        </div>
      ) : null}

      {/*
        `min-w-0` and an explicit single column. A CSS grid track defaults to
        `auto`, which resolves to MAX-CONTENT — so one wide panel (here, the
        services table) stretches the whole grid and pushes the page past a
        390px viewport. Without this, `overflow-x-auto` inside the table never
        gets the chance to do its job. Found by the browser check.
      */}
      <div className="grid w-full min-w-0 max-w-4xl grid-cols-1 gap-gutter">
        <Panel title={t('quotations.prospect')}>
          {/*
            READ-ONLY, and deliberately so. These are the details the client was
            quoted under; retyping them here would be exactly the re-entry the
            acceptance criterion forbids.
          */}
          <div className="grid gap-stack sm:grid-cols-2">
            <ReadOnlyField label={t('quotations.prospectName')}>
              {record.party.name ??
                t('common.notRecorded')}
            </ReadOnlyField>
            <ReadOnlyField label={t('quotations.prospectContactName')}>
              {record.party.contactName ?? t('common.notRecorded')}
            </ReadOnlyField>
            <ReadOnlyField label={t('quotations.prospectEmail')}>
              {record.party.contactEmail ? (
                <span dir="ltr">{record.party.contactEmail}</span>
              ) : (
                t('common.notRecorded')
              )}
            </ReadOnlyField>
            <ReadOnlyField label={t('quotations.prospectPhone')}>
              {record.party.contactPhone ? (
                <span dir="ltr">{record.party.contactPhone}</span>
              ) : (
                t('common.notRecorded')
              )}
            </ReadOnlyField>
          </div>
        </Panel>

        {search && search.matches.length > 0 ? (
          <Panel title={t('clients.duplicate.matchesFound')}>
            <DuplicateMatchList
              matches={search.matches}
              truncated={search.candidatesTruncated}
              renderAction={(match) => (
                <div className="flex flex-col items-end gap-inline">
                  {/*
                    Acknowledged BY ID, one candidate at a time. A blanket
                    confirmation would wave through a second candidate that
                    appeared while the page was open (decision D36).
                  */}
                  <CheckboxField
                    label={t('clients.duplicate.acknowledge')}
                    value={acknowledged.includes(match.clientId)}
                    onChange={(checked) =>
                      setAcknowledged((current) =>
                        checked
                          ? [...new Set([...current, match.clientId])]
                          : current.filter(
                              (candidate) => candidate !== match.clientId,
                            ),
                      )
                    }
                  />
                  {/*
                    The commonest real outcome of a duplicate warning is not
                    "create a second record anyway" — it is "that is them".
                  */}
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    isLoading={linkExisting.isPending}
                    onPress={() => linkExisting.mutate(match.clientId)}
                  >
                    {t('common.continue')}
                  </ActionButton>
                </div>
              )}
            />
            <div className="mt-stack">
              <TextAreaField
                label={t('common.reasonOptional')}
                value={note}
                onChange={setNote}
                rows={2}
              />
            </div>
          </Panel>
        ) : null}

        {/* The same footer as every form: Cancel, then the one primary act. */}
        <div className="border-t border-default-200 pt-stack">
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => navigate(`/quotations/${record.id}`)}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!search}
              isLoading={convert.isPending}
              onPress={() => convert.mutate()}
            >
              {t('quotations.convert')}
            </ActionButton>
          </ActionBar>
        </div>
      </div>
    </>
  );
}
